import React from "react";
import { useNavigate } from "react-router-dom";
import { Table, Select, Input, Empty, Tooltip } from "antd";
import InvoiceDocLink from "../../../components/ui/InvoiceDocLink";
import DateFilter from "../../../components/ui/DateFilter";
import { formatAmount, getMaxDecimalsForCurrency } from "../../../utils";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "../../../utils/constants";
import Badge from "../../../components/ui/Badge";

const TABS = ["All","Created" ,"Pending","In Progress", "Paid",];

const DESCRIPTION_CHAR_LIMIT = 25;

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const InvoiceHistoryList = ({
  data,
  loading,
  total,
  page,
  pageSize,
  status,
  onStatusChange,
  search,
  onSearchChange,
  onPageChange,
  onPageSizeChange,
  onDateFilterChange,
  disableDateFilter,
}) => {
  const navigate = useNavigate();

  const columns = [
     {
      title: "SN",
      key: "sn",
      width: 60,
      render: (_, __, index) => (page - 1) * pageSize + index + 1,
    },
    {
      title: "Invoice ID",
      key: "invoice_id",
      render: (_, inv) => inv.invoiceId || "—",
    },
    {
      title: "Amount",
      key: "amount",
      render: (_, inv) => (
        <span className="font-medium text-gray-900">
          {formatAmount(inv.amount, getMaxDecimalsForCurrency("USD"))}
        </span>
      ),
    },
    {
      title: "Raised Date",
      key: "raised_date",
      render: (_, inv) => <span className="whitespace-nowrap">{formatDate(inv.createdAt)}</span>,
    },
    {
      title: "Due Date",
      key: "due_date",
      render: (_, inv) => (
        <span className="whitespace-nowrap">{inv.dueDate ? formatDate(inv.dueDate) : "—"}</span>
      ),
    },
    {
      title: "Status",
      key: "status",
      render: (_, inv) => (
        <Badge status={inv.status || "pending"} />
      ),
    },
    {
      title: "Document",
      key: "document",
      render: (_, inv) => <InvoiceDocLink invoiceId={inv.id} docUrl={inv.docUrl} />,
    },
    {
      title: "Description",
      key: "description",
      width: 220,
      render: (_, inv) => {
        const comment = inv.comment || "—";
        if (comment.length <= DESCRIPTION_CHAR_LIMIT) return comment;
        return (
          <Tooltip title={comment}>
            <span className="cursor-help">{comment.slice(0, DESCRIPTION_CHAR_LIMIT)}…</span>
          </Tooltip>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      align: "right",
      render: (_, inv) =>
        inv.status === "created" ? (
           <button
          onClick={() => navigate(`/sender/invoices/${inv.id}/pay`)}
          className="bg-brand text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-brandHover transition-colors whitespace-nowrap"
        >
          Pay Now
        </button>
        ) : (
          <button
            onClick={() => navigate(`/sender/invoices/${inv.id}/pay`)}
            className="text-sm text-brand hover:underline whitespace-nowrap"
          >
            View →
          </button>
        ),
    },
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-xl">
      <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-gray-100 flex-wrap">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold text-gray-800 m-0">All Invoices</h2>
          {!loading && total > 0 && (
            <span className="text-xs font-medium bg-gray-100 text-gray-600 px-2.5 py-0.5 rounded-full border border-gray-200">
              {total.toLocaleString()} total
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input.Search
            placeholder="Search by Invoice ID or…"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            allowClear
            className="w-60"
          />
          {!disableDateFilter && <DateFilter onChange={onDateFilterChange} />}
          <Select
            value={status}
            onChange={onStatusChange}
            showSearch={false}
            className="min-w-[150px]"
            options={TABS.map((t) => ({ value: t, label: t }))}
          />
        </div>
      </div>

      <Table
        className="admin-table table-blue-zebra"
        columns={columns}
        dataSource={data}
        rowKey={(inv) => inv.id}
        loading={loading}
        locale={{
          emptyText: loading ? (
            <div className="py-12" />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Paid invoices will appear here."
            />
          ),
        }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: { showSearch: false },
          showQuickJumper: false,
          pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS.map(String),
          responsive: true,
          onShowSizeChange: (_current, newSize) => onPageSizeChange(newSize),
          onChange: (newPage, newPageSize) => {
            onPageChange(newPage);
            if (newPageSize && newPageSize !== pageSize) onPageSizeChange(newPageSize);
          },
          showTotal: (totalCount, range) => (
            <span className="text-xs text-gray-500 whitespace-nowrap">
              Showing{" "}
              <span className="font-medium text-gray-800">
                {range[0]}-{range[1]}
              </span>{" "}
              of <span className="font-medium text-gray-800">{totalCount}</span> invoices
            </span>
          ),
        }}
      />
    </div>
  );
};

export default InvoiceHistoryList;
